import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { products as localProducts } from '../data/products';
import { productsApi } from '../services/api';
import { Product } from '../types';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import ScrollFadeHeading from './ScrollFadeHeading';

// Skeleton card shown while products load from backend
const ProductSkeleton: React.FC = () => (
  <div className="bg-cream-50 rounded-2xl shadow-card border border-coffee-100/10 overflow-hidden animate-pulse">
    <div className="h-64 bg-cream-200" />
    <div className="p-6 space-y-3">
      <div className="h-5 bg-cream-200 rounded w-3/4" />
      <div className="h-4 bg-cream-200 rounded w-full" />
      <div className="h-4 bg-cream-200 rounded w-5/6" />
      <div className="space-y-2 pt-2">
        <div className="h-3 bg-cream-200 rounded w-1/2" />
        <div className="h-3 bg-cream-200 rounded w-1/2" />
        <div className="h-3 bg-cream-200 rounded w-1/2" />
      </div>
      <div className="h-10 bg-cream-200 rounded mt-4" />
    </div>
  </div>
);

interface ProductCatalogProps {
  onQuoteClick: () => void;
}

const ProductCatalog: React.FC<ProductCatalogProps> = ({ onQuoteClick }) => {
  const { t, i18n } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [products, setProducts] = useState<Product[]>(localProducts);
  const [loading, setLoading] = useState(true);
  const [hasApiError, setHasApiError] = useState(false);

  // Load products from backend, fallback to local data
  useEffect(() => {
    productsApi.getAll()
      .then((data) => {
        if (data && data.length > 0) {
          const mapped: Product[] = data.map((p: any) => ({
            id: p.slug ?? p.id,
            name: p.name,
            category: p.category,
            grade: p.grade ?? undefined,
            image: p.image,
            images: p.images ?? [],
            specifications: (p.specifications ?? []).map((s: any) => ({
              name: s.name,
              value: s.value,
              unit: s.unit ?? undefined,
            })),
            description: { fr: p.description_fr, en: p.description_en },
          }));
          setProducts(mapped);
        }
      })
      .catch(() => {
        setHasApiError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    { id: 'all', name: 'Tous', nameEn: 'All' },
    { id: 'vanilla', name: 'Vanille', nameEn: 'Vanilla' },
    { id: 'coffee', name: 'Café', nameEn: 'Coffee' },
    { id: 'spices', name: 'Épices', nameEn: 'Spices' },
    { id: 'cocoa', name: 'Cacao', nameEn: 'Cocoa' },
    { id: 'essential-oils', name: 'Huiles Essentielles', nameEn: 'Essential Oils' }
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(product => product.category === activeCategory);

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };

  return (
    <section id="products" className="section-shell py-24">
      <div className="container mx-auto px-4">
        <ScrollFadeHeading className="mb-12 flex flex-col gap-5 lg:mb-16 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="eyebrow mb-4">{t('products.eyebrow', 'Sélection à la source')}</p>
            <h2 className="max-w-xl font-display text-4xl font-bold text-coffee-200 md:text-5xl">
              {t('products.title')}
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-coffee-100">
            {t('products.subtitle', 'Des matières premières choisies pour leur origine, leur régularité et leur potentiel.')}
          </p>
        </ScrollFadeHeading>

        {/* Category Filter */}
        <div className="mb-10 flex flex-wrap gap-2" role="tablist" aria-label={t('products.filterAria', 'Filtrer les produits')}>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              role="tab"
              aria-selected={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`rounded-full px-4 py-2.5 text-sm font-bold transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-coffee-200 text-cream-50 shadow-soft'
                  : 'border border-coffee-200/15 bg-cream-50/60 text-coffee-100 hover:border-forest-200/40 hover:text-forest-200'
              }`}
            >
              {i18n.language.startsWith('en') ? category.nameEn : category.name}
            </button>
          ))}
        </div>

        {hasApiError && !loading && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-saffron/30 bg-saffron/10 px-4 py-3 text-sm text-coffee-100">
            <span>{t('products.offline', 'Catalogue local affiché. Les disponibilités peuvent être mises à jour.')}</span>
            <span className="shrink-0 font-bold text-saffron">{t('products.offlineShort', 'Hors ligne')}</span>
          </div>
        )}

        {/* Products Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3"
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <ProductSkeleton key={i} />)
            : filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  layout
                  className="h-full"
                >
                  <ProductCard
                    product={product}
                    onSelect={handleProductSelect}
                  />
                </motion.div>
              ))
          }
        </motion.div>

        {!loading && filteredProducts.length === 0 && (
          <div className="surface-panel rounded-2xl px-6 py-16 text-center">
            <p className="eyebrow mb-3">{t('products.emptyLabel', 'Aucun résultat')}</p>
            <h3 className="font-display text-2xl font-bold text-coffee-200">{t('products.emptyTitle', 'Cette sélection arrive bientôt.')}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-coffee-100">{t('products.emptyText', 'Écrivez-nous pour connaître les prochaines disponibilités.')}</p>
          </div>
        )}

        {/* Product Categories Overview */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center p-8 bg-cream-50 rounded-lg"
          >
            <div className="w-24 h-24 bg-forest-200 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
              <img src="/assets/vanilla_GradeA_gourmet.jpeg" alt="Vanille" className="w-30 h-30 rounded-full object-cover" />
            </div>
            <h3 className="font-display text-xl font-bold text-coffee-200 mb-2">
              {t('products.vanilla.title')}
            </h3>
            <p className="text-coffee-100 text-sm">
              {t('products.vanilla.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-center p-8 bg-cream-50 rounded-lg"
          >
            <div className="w-24 h-24 bg-forest-200 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
              <img src="/assets/coffee_cup.jpg" alt="Café" className="w-24 h-24 rounded-full object-cover" />
            </div>
            <h3 className="font-display text-xl font-bold text-coffee-200 mb-2">
              {t('products.coffee.title')}
            </h3>
            <p className="text-coffee-100 text-sm">
              {t('products.coffee.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center p-8 bg-cream-50 rounded-lg"
          >
            <div className="w-24 h-24 bg-forest-200 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
              <img src="/assets/cloves.jpeg" alt="Épices" className="w-24 h-24 rounded-full object-cover" />
            </div>
            <h3 className="font-display text-xl font-bold text-coffee-200 mb-2">
              {t('products.spices.title')}
            </h3>
            <p className="text-coffee-100 text-sm">
              {t('products.spices.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center p-8 bg-cream-50 rounded-lg"
          >
            <div className="w-24 h-24 bg-forest-200 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
              <img src="/assets/vanilla_flowers.jpeg" alt="Huiles Essentielles" className="w-24 h-24 rounded-full object-cover" />
            </div>
            <h3 className="font-display text-xl font-bold text-coffee-200 mb-2">
              {t('products.essential-oils.title')}
            </h3>
            <p className="text-coffee-100 text-sm">
              {t('products.essential-oils.description')}
            </p>
          </motion.div>
        </div> */}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal 
          product={selectedProduct} 
          onClose={closeModal}
          onQuoteClick={onQuoteClick}
        />
      )}
    </section>
  );
};

export default ProductCatalog;